// request-error.js — the fix for the bug this whole task exists to fix.
//
// `request()` (src/config/helpers/axios-instance.js) is built on
// `client(options).then(onSuccess).catch(onError)`, and BOTH `onSuccess` and
// `onError` *return* a value rather than re-throwing — so the promise
// `request()` hands back always resolves, never rejects, no matter what
// happens on the wire. On any non-401 error it also fires a synchronous,
// unconditional `window.location.replace('/404')` before returning the raw
// error object; that redirect is baked into the shared helper and nothing a
// hook does can stop it.
//
// The concrete, provable consequence: if a hook wraps `request()` directly
// and hands its resolved value straight to react-query as the query result
// (as `GetGLP1Orders` in src/hooks/fetch-hooks/get-glp1-orders/get-glp1-orders.js
// does), `useQuery`'s `isError` can NEVER become true — the promise never
// rejects, so react-query always sees a "success". See
// src/pages/glp1-dashboard/index.jsx:206-210 for the dead `{isError && ...}`
// branch this produces.
//
// The fix: every query/mutation fn in src/v2/hooks/ must inspect what
// `request()` resolved to and `throw` it itself when that value is actually
// an error. Throwing inside the fn passed to useQuery/useMutation is what
// makes react-query set `isError`/`error` correctly, run its own `onError`
// callbacks, etc. — it does NOT stop the `/404` redirect from having already
// fired for a real non-401 failure (nothing can), but it does mean the
// *query* ends up in a genuine error state instead of a false "success" that
// tries to render `undefined.data.whatever`.
//
// Detection: a successful `request()` resolves to the underlying axios
// response — an object with a numeric `.status` in the 2xx range and a
// `.data` payload. A failed `request()` resolves to the raw axios error
// object instead, which reliably carries `.isAxiosError === true` (axios
// stamps every error it produces, including plain network errors with no
// `.response` at all — exactly what this dev environment will produce,
// since there is no server behind `/api/ops/tat/*`). We treat anything that
// isn't a well-formed 2xx response as an error, which also defensively
// covers `undefined`/`null` and any other shape that isn't a real success.

/**
 * @param {unknown} result - whatever `request()` resolved to.
 * @returns {boolean} true if `result` is an error, not a genuine 2xx response.
 */
export function isRequestError(result) {
    if (!result || typeof result !== "object") return true;
    if (result.isAxiosError) return true;
    const status = result.status;
    if (typeof status !== "number" || status < 200 || status >= 300) return true;
    return false;
}

/**
 * Throws `result` if it's error-shaped, otherwise returns it unchanged.
 * Call this immediately after every `await request(...)` in a query or
 * mutation fn — see useTatOrders.js / useTatStats.js / useTatFilters.js /
 * useSetErrorTag.js for the (identical, four-line) call site.
 *
 * @template T
 * @param {T} result
 * @returns {T}
 */
export function throwIfRequestError(result) {
    if (isRequestError(result)) throw result;
    return result;
}
